import type { TFunction } from 'i18next';
import { MAX_DOMAIN_CHAR } from 'src/content/SignUp/utils';
import { VALID_SUBDOMAIN_REGEX } from 'src/utils/constants';
import { TRANSLATION_CONSTANTS as T } from 'src/utils/translations';
import z from 'zod';

export type PasswordSettingFormValues = {
  password: string;
  confirmPassword: string;
  tenantDomain: string;
};

/** Single source of truth for the password policy: drives both the schema and the criteria checklist. */
export const PASSWORD_RULES = [
  {
    key: T.passwordRequirementSpecial,
    isValid: (value: string) =>
      /[!@#$%^&*(),.?":{}|<>_\-+=[\]\\/;'`~]/.test(value)
  },
  {
    key: T.passwordRequirementUppercase,
    isValid: (value: string) => /[A-Z]/.test(value)
  },
  {
    key: T.passwordRequirementLowercase,
    isValid: (value: string) => /[a-z]/.test(value)
  },
  {
    key: T.passwordRequirementNumber,
    isValid: (value: string) => /\d/.test(value)
  },
  {
    key: T.passwordRequirementMinLength,
    isValid: (value: string) => value.length >= 8
  }
] as const;

/** Format rules for the sub-domain the user types (the `.${POST_DOMAIN_PART}` suffix is not part of it). */
export const tenantDomainSchema = (t: TFunction) =>
  z
    .string()
    .trim()
    .min(1, t(T.domainNameRequired))
    .max(MAX_DOMAIN_CHAR, t(T.domainNameLength))
    .regex(VALID_SUBDOMAIN_REGEX, t(T.domainInvalidFormat));

export const passwordSettingSchema = (t: TFunction, isSuggested: boolean) =>
  z
    .object({
      password: z.string().superRefine((password, ctx) => {
        PASSWORD_RULES.forEach(({ key, isValid }) => {
          if (!isValid(password)) {
            ctx.addIssue({ code: 'custom', message: t(key) });
          }
        });
      }),
      confirmPassword: z.string(),
      // The domain field is only rendered (and only submitted) when the backend suggests one.
      // Format only — availability is an async answer, applied via setError in usePasswordSetting.
      tenantDomain: isSuggested ? tenantDomainSchema(t) : z.string()
    })
    .superRefine(({ password, confirmPassword }, ctx) => {
      if (password !== confirmPassword) {
        ctx.addIssue({
          code: 'custom',
          path: ['confirmPassword'],
          message: t(T.passwordRequirementMatch)
        });
      }
    });
